import React from 'react'
import styles from './UIGameEndFooter.module.scss'
import {Button} from '../../../common/Button/Button'
import {T} from '../../../common/T'
import {useUIGameEndContext} from '../../../contexts/UIGameEndContext'
import {GameTypeEnum, ModeSettingEnum} from '@colonist/types'
import {EndGameButtonTypes, getEndGameRightButtonType} from '@colonist/game-endgame-types'
import {TranslationObject} from '@colonist/i18n'
import clsx from 'clsx'
import {FADE_IN_ANIMATION_DELAYS} from '../constants'
import {FadeInContainer} from '../../../common/FadeInContainer'
import {useGameStore} from '../../../store/GameStoreContext'
import {useIsReplay} from '../../../store/selectors/useIsReplay'
// eslint-disable-next-line no-restricted-imports
import {ClientUserState} from '../../../../../../code/client/shared/scripts/ClientUserState'

interface UIGameFooterProps {
    disableAnimation: boolean
}

export function UIGameEndFooter({
    disableAnimation,
}: UIGameFooterProps) {
    const gameSettings = useGameStore((state) => state.gameSettings)
    const userStates = useGameStore((state) => state.gameUserStates.userStates)
    const isReplay = useIsReplay()
    const {
        onClickRematch,
        onClickPlayAgain,
        onClickRetryBeginnerMode,
        onClickNextLevelBeginnerMode,
        onClickHome,
        numberOfPlayersJoinedRematch,
        playersByRank,
        isPlayerHasRankedState,
        isAfterLiveGame,
    } = useUIGameEndContext()

    const myUserId = ClientUserState.userState?.id
    const isMyUserIdPlaying = myUserId !== undefined && userStates.some((userState) => userState.userId === myUserId)

    const renderRightButton = () => {
        if(!isMyUserIdPlaying) return null
        if(!(isAfterLiveGame || !isReplay)) return null

        const isCampaign = gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        const isMatchmakingGame = gameSettings.gameType == GameTypeEnum.MatchmakingGame || gameSettings.gameType == GameTypeEnum.MatchmakingGameRanked

        const winningPlayerColor = playersByRank[0].color
        const winningUser = userStates.find((userState) => userState.selectedColor === winningPlayerColor)
        const isWonByHuman = winningUser?.isBot === false

        const rightButton: EndGameButtonTypes = getEndGameRightButtonType(isCampaign, isMatchmakingGame, isWonByHuman)

        let translation: TranslationObject = {key: 'strings:game.endGame.footer.continue'}
        let onClickAction
        switch(rightButton) {
            case EndGameButtonTypes.Rematch:
                translation = numberOfPlayersJoinedRematch ? {
                    key: 'strings:game.endGame.footer.playersJoined',
                    options: {
                        playerCount: numberOfPlayersJoinedRematch,
                        maxPlayerCount: gameSettings.maxPlayers,
                    },
                } : {key: 'strings:game.endGame.footer.rematch'}
                onClickAction = onClickRematch
                break
            case EndGameButtonTypes.MatchmakingPlayAgain:
                translation = {key: 'strings:game.endGame.footer.play'}
                onClickAction = onClickPlayAgain
                break
            case EndGameButtonTypes.Retry:
                translation = {key: 'strings:game.endGame.footer.retry'}
                onClickAction = onClickRetryBeginnerMode
                break
            case EndGameButtonTypes.NextLevel:
                translation = {key: 'strings:game.endGame.footer.nextLevel'}
                onClickAction = onClickNextLevelBeginnerMode
                break
            default:
                return null
        }

        return <Button className={styles.button} onClick={onClickAction}>
            <T id={translation} />
        </Button>
    }

    return (
        <FadeInContainer
            animationDelayMs={isPlayerHasRankedState && !isReplay ? FADE_IN_ANIMATION_DELAYS.footerRankedGame : FADE_IN_ANIMATION_DELAYS.footer}
            animationDurationMs={FADE_IN_ANIMATION_DELAYS.transitionDuration}
            className={styles.footerContainer}
            disableAnimation={disableAnimation}>
            <div className={clsx(styles.buttons)}>
                <Button
                    className={styles.button}
                    onClick={onClickHome}
                    variant='dark'>
                    <T id={{key: 'strings:game.endGame.footer.home'}} />
                </Button>
                {renderRightButton()}
            </div>
        </FadeInContainer>
    )
}
